import React from 'react'
import {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentRect,
  GeckoComponentShape,
  GeckoFeature,
  GeckoLegacyBoilerWall,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoMeasurementRange,
  GeckoPhotogrammetryModel,
  GeckoPhotogrammetryTextureModel,
  GeckoPipeSection,
  GeckoShellCourse,
} from 'types'

import { ClipConfig } from './systems/clipConfig'

/** The current mode AssetViz is set to, determining its behavior and callbacks */
export enum AssetVizMode {
  NoInteraction,
  // Selection modes
  SelectBin,
  SelectPoint,
  SelectRect,
  SelectMarkupRect,
  // Markup creation
  CreateMarkupRect,
  CreateMarkupIcon,
  CreateMarkupLabel,
  // Feature editing
  AllFeaturesSelectable,
  // Screenshot
  Screenshot,
}

/** Info about what the user's mouse is currently over */
export type AssetVizHoverEvent = {
  mouseX: number
  mouseY: number
  positionX: number
  positionY: number
  positionZ: number
  coords: GeckoComponentCoords
  shellCourse: GeckoShellCourse | undefined
  waterwall: GeckoLegacyBoilerWall | undefined
  componentDataFrame: GeckoComponentDataFrame | undefined
  componentModel: GeckoComponentModel | undefined
  markupPrimitive: GeckoMarkupPrimitive | undefined
}

/**
 * A set of colors used for the surface and edges of an asset
 */
export type AssetVizColorProps = {
  /** An optional override to the base color of all formatters */
  assetColor?: string

  /** An optional override to the grid color used on formatters */
  gridColor?: string

  /** An optional override to the color used for selection, etc. outlines */
  outlineColor?: string

  /** An optional override for the canvas background color. */
  backgroundColor?: string
}

export enum AssetVizHeatmapDisplayState {
  Sharp = 'sharp',
  Smooth = 'smooth',
  Off = 'off',
}

/**
 * An object which allows users to configure how the current scene is rendered.
 * Note that not all configurations are valid for all scenes; consult
 * AssetVizSceneConfigOptions to know what is available for the current scene.
 */
export type AssetVizSceneConfig = {
  /** The dimensionality of this scene (rolled out flat, 3D, etc.) */
  show3D: boolean

  /** Whether to show rulers or not. Defaults to not showing rulers */
  showRulers:
    | boolean
    | ((component: GeckoComponentModel | undefined) => boolean)

  /** Whether to show compass or not. Defaults to not showing compass */
  showCompass: boolean | ((component: GeckoComponentModel) => boolean)

  /** Display state of the heatmap. Defaults to sharp. */
  heatmapDisplayState: AssetVizHeatmapDisplayState

  /** Opacity setting for the heatmap texture */
  heatmapOpacity:
    | number
    | ((componentModel: GeckoComponentModel | undefined) => number)

  /** Opacity setting for asset, excluding heatmap */
  assetOpacity:
    | number
    | ((componentModel: GeckoComponentModel | undefined) => number)

  /** Whether to show shell courses, waterwalls, manways, etc. */
  showFeatures:
    | boolean
    | ((props: {
        feature: GeckoFeature
        componentModel: GeckoComponentModel | undefined
      }) => boolean)

  /** Whether to show labels on vertical welds, plates, manways, nozzles, and fixedSensors. */
  showFeatureLabels:
    | boolean
    | ((
        props:
          | {
              feature: GeckoFeature
              componentModel: GeckoComponentModel | undefined
            }
          | {
              feature: GeckoFeature | 'flow_direction'
              componentModel: GeckoPipeSection | undefined
            }
      ) => boolean)

  // Debug settings ------------------------------------------------------------

  /** Whether to show debug colors behind HTML overlays. */
  showHtmlOverlayDebugColors: boolean

  /** Whether to show react-three-drei debug stats. */
  showDebugStats: boolean

  /** Whether to show random debug colors on RAM features. */
  showFeatureDebugColors: boolean

  /** Whether to show random debug colors specifically on shell courses. */
  showShellCourseDebugColors: boolean

  /** Whether to show random debug colors specifically on plate features. */
  showPlateFeatureDebugColors: boolean

  /** Whether to show bin values directly on the component surface. */
  showBinValues: boolean
} & AssetVizScreenshotConfig &
  AssetVizPhotogrammetryConfig

/**
 * An object for screenshot-specific configuration.
 */
export type AssetVizScreenshotConfig = {
  /** Whether to show the legend. */
  showLegend: boolean

  /** An override to the viewport aspect ratio. */
  overrideCameraAspectRatio: React.MutableRefObject<number> | undefined

  /** Whether to force the display into the 11x7 aspect ratio. */
  force11x7AspectRatio: boolean

  /** Whether to stretch the asset to fit the canvas size. */
  stretchAssetToFitScreen: boolean

  /** Whether to invert the canvas and text colors. */
  invertColors: boolean

  /** Consolidated clipping configuration. */
  clipConfig: ClipConfig
}

/** Config piped through from PhotogrammetryContext for use within AssetViz. */
export type AssetVizPhotogrammetryConfig = {
  /** The actively selected photogrammetry model to display. */
  activePhotogrammetryModel: GeckoPhotogrammetryModel | undefined

  /** The actively selected photogrammetry texture to use. */
  activePhotogrammetryTexture: GeckoPhotogrammetryTextureModel | undefined

  /** Whether to show a photogrammetry texture. */
  showPhotogrammetryTexture: boolean

  /** Function to set whether the active photogrammetry texture is loading. */
  setPhotogrammetryTextureLoading:
    | React.Dispatch<React.SetStateAction<boolean>>
    | undefined

  /** Whether the active photogrammetry texture has updates to propagate. */
  hasPhotogrammetryTextureUpdates: boolean
}

/**
 * An object describing what AssetVizSceneConfig options are valid for the
 * currently rendered scene. Note that this will update if the scene changes.
 */
export type AssetVizSceneConfigOptions = {
  /** Whether AssetViz has enough information to render in 3D */
  threeDimensionsEnabled: boolean

  /** Whether AssetViz has enough information to shell courses, waterwalls, etc. */
  featuresEnabled: boolean
}

/**
 * An AssetViz "controller" object passed back via ref to allow users to invoke
 * various methods such as zoomToFit(). The imperative way to control AssetViz.
 */
export type AssetVizController = {
  /**
   * A callback which allows the user to zoom to a specific part of their asset
   * @param rect A range of coords to zoom to (and fit on-screen)
   */
  zoomToFit: (rect: GeckoComponentRect) => Promise<void>

  /**
   * A callback which allows the user to instantly snap to a specific part of their asset
   * without animation
   * @param rect A range of coords to snap to (and fit on-screen)
   * @returns A promise that resolves immediately after the position update
   */
  snapToFit: (rect: GeckoComponentRect) => Promise<void>

  /** A simple method to center on a rect preserving current zoom */
  centerOn: (rect: GeckoComponentRect) => Promise<void>

  /** A simple method to cancel any ongoing zoom effects */
  cancelZoom: () => void

  /** A simple method to reset camera to starting position*/
  resetCamera: () => void

  /** A method to take a screenshot of the canvas */
  takeScreenshot: () => Promise<Blob>
}

/**
 * Properties passed into a single AssetViz component, used to determine what
 * to render, how to render it, etc.- even providing callbacks so that AssetViz
 * interactions can affect the rest of the application.
 */
export type AssetVizProps = {
  // Rolodex data --------------------------------------------------------------
  id?: string
  setAssetImage?: React.Dispatch<React.SetStateAction<string | null>>
  /** An optional asset model to allow rich asset model features */
  assetModel: GeckoAssetModel | undefined

  /** The known Rolodex formatters for the provided GeckoUnit */
  components: ReadonlyArray<GeckoComponent> | undefined

  /** The threshold range for this view selected by the CDF */
  colorThreshold: GeckoColorScaleThreshold | undefined

  /** All heatmap data to draw on this asset -- only one will render per component*/
  componentDataFrames: Array<GeckoComponentDataFrame> | undefined

  /** All markup layers to draw on this asset */
  markupLayers?: ReadonlyArray<GeckoMarkupLayer>

  /** Markup layer label visibility state - array of layer IDs where labels are hidden */
  markupLayerLabelVisibility?: ReadonlyArray<string>

  /** Whether we're in Multi Component Viewer mode -- undefined assumes false*/
  showMultiComponentView?: boolean

  /** Show axes helper -- only in multi component viewer*/
  showAxesHelper?: boolean

  // Display -------------------------------------------------------------------

  /** Given a cdf, the range to filter down to, if any */
  cdfToFilteredValueRange?: (
    cdf: GeckoComponentDataFrame
  ) => GeckoMeasurementRange | undefined

  /** Given a cdf, the categorical values to filter to, if any */
  cdfToSelectedCategoricalValues?: (
    cdf: GeckoComponentDataFrame
  ) => number[] | undefined

  /** The "scene" to show this asset in (rolled out flat, 3D, etc.) */
  sceneConfig?: Partial<AssetVizSceneConfig> | undefined

  /**
   * An optional callback letting the caller know what scenes this asset
   * can be displayed in (rolled out flat, 3D, etc.)
   */
  onSceneConfigOptionsChanged?: React.Dispatch<
    React.SetStateAction<AssetVizSceneConfigOptions>
  >

  /** React Component to render as the VizToolTip. If undefined, VizToolTip will not be visible.*/
  vizToolTipContent?: React.ReactNode | undefined

  /**
   * An optional multiplier to apply to the camera's fit calculation.
   * Multiplied to the bounding box dimensions
   */
  cameraFitMultiplier?: number

  // User interaction ----------------------------------------------------------

  /** How the user interacts with AssetViz: selecting bins, rects, etc. */
  mode?: AssetVizMode

  /**
   A callback which allows users to hover over regions of an asset
   * @param vizTooltipEvent Information about what was hovered
   * @param event Information about what was hovered
   */
  onHovered?: (
    tooltipFrozen: boolean,
    event: AssetVizHoverEvent | undefined
  ) => void

  /** An optional rect to consider "selected" on this unit's surface */
  selection?:
    | GeckoComponentShape
    | GeckoMarkupPrimitive
    | GeckoFeature
    | GeckoMarkupLayer

  /**
   * A callback which allows users to click and select regions on an asset
   * @param selection Information about what was selected
   */
  onSelected?: (
    selection:
      | GeckoComponentShape
      | GeckoMarkupPrimitive
      | GeckoFeature
      | undefined
  ) => void

  /**
   * A callback representing what the next selection would look like if a user
   * was to click right now (very similar to a "hovered" callback)
   * @param selection Information about what the user is hovering over
   */
  onDraftSelected?: (
    selection:
      | GeckoComponentShape
      | GeckoMarkupPrimitive
      | GeckoFeature
      | undefined
  ) => void

  /**
   * A callback notifying that an edit action has occurred within AssetViz
   * @param selection The object which was edited (including its edits)
   */
  onWrite?: (selection: GeckoMarkupPrimitive) => Promise<void>

  /**
   * Refreshes data and then selects the specified layer (deselecting any
   * previously selected primitives)
   * @param markupLayer The layer to select
   * @returns A promise that resolves once the data is refreshed and the layer is selected
   */
  refreshAndSelectLayer?: (markupLayer: GeckoMarkupLayer) => Promise<void>

  /**
   * Deletes the specified markup primitive in Rolodex.
   * @param primitive The primitive to delete
   * @param markupLayer The markup layer to which this primitive belongs
   * @returns A promise that resolves when the deletion and subsequent refresh have finished
   */
  deletePrimitive?: (
    primitive: GeckoMarkupPrimitive,
    markupLayer: GeckoMarkupLayer
  ) => Promise<void>

  // Styling -------------------------------------------------------------------

  /** An optional component to show while AssetViz is still loading */
  loadingView?: React.ReactNode

  /** Optional flag to allow the asset viz to render when not in view, used for automations outside of user's view */
  offscreenRender?: boolean
} & AssetVizColorProps
